import {
  cloneElement,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from './cn';

type InjectableProps = { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean };

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactElement<InjectableProps>;
}

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
  className,
  ...rest
}: FieldProps) {
  const generatedId = useId();
  const childId = isValidElement(children) ? (children.props as InjectableProps).id : undefined;
  const controlId = htmlFor ?? childId ?? generatedId;

  const hintId = hint && !error ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = errorId ?? hintId;

  const child = isValidElement(children)
    ? cloneElement<InjectableProps>(children, {
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })
    : children;

  return (
    <div className={cn('ui-field', className)} {...rest}>
      <label htmlFor={controlId} className="ui-field__label">
        {label}
        {required ? (
          <span className="ui-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {child}
      {error ? (
        <p id={errorId} className="ui-field__message ui-field__message--error">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="ui-field__message">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
